import React from 'react';
import Label from 'components/form/Label/Label';
import { Button } from 'components/form/Button';
import DifferentialText from 'components/DifferentialText';
import './StandardAutoLoanOffer.scss';
import { Row } from 'components/Containers/Row/Row';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';
import SectionContainer from 'components/Containers/SectionContainer/index';
import { BulletText } from 'partial/OfferDetails/components/BulletCollapse/BulletCollapse';
import classnames from 'classnames';
import { Image } from 'components/Image/Image';
import { commafy } from 'helpers/number';
import { useConfig } from 'hook/config.hook';
import { Link } from 'components/navigation/Link/Link';

const AutoLoanOfferImage = ({ datatestid, clickApplyHandler, imageLink }) => {
  return (
    <FlexContainer className="autoLoanImageHolder">
      <img
        data-testid={datatestid ? `${datatestid}-loanImage` : null}
        alt="autoloanImage"
        onClick={clickApplyHandler}
        src={imageLink}
        className="autoLoanImage link"
      />
    </FlexContainer>
  );
};

const AutoLoanOfferHeader = ({
  datatestid,
  imageLink,
  clickApplyHandler,
  name,
}) => {
  return (
    <Row className="autoLoanHeaderHolder">
      <FlexContainer className="autoLoanApplyHolder justify-between flex-row">
        <AutoLoanOfferImage
          datatestid={datatestid}
          clickApplyHandler={clickApplyHandler}
          imageLink={imageLink}
        />
        <AutoLoanOfferApply
          datatestid={datatestid}
          clickApplyHandler={clickApplyHandler}
          name={name}
        />
      </FlexContainer>
      <div className="padB24 border-bottom"></div>
    </Row>
  );
};

const AutoLoanOfferApply = ({ datatestid, clickApplyHandler, name }) => {
  const { config } = useConfig();
  return (
    <FlexContainer className="flex-column autoloanApply">
      <Button
        className="applyNowButton"
        mrphType="primary"
        rounded
        data-testid={datatestid ? `${datatestid}-apply-label` : null}
        onClick={clickApplyHandler}
        datatestid={datatestid ? `${datatestid}-ApplyNowButton` : null}
        role="presentation"
      >
        <FlexContainer className="justify-center align-items-center">
          <Image imgClass={'standardLockIcon'} />
          <Label
            datatestid={`${datatestid}-applyButtonText`}
            type="Subtitle-2"
            color="primary_cta_text_light"
            tabIndex="-1"
          >
            Apply Now
          </Label>
        </FlexContainer>
      </Button>
      <div
        className="thirdPartyApplyNote text-center"
        data-testid={datatestid ? `${datatestid}-applyNowText` : null}
      >
        on {name}'s {config?.secureWebsiteDisclaimer}
      </div>
    </FlexContainer>
  );
};

const AutoLoanOfferFooter = ({ datatestid, clickDetailsHandler, tcUrl }) => {
  return (
    <Row className="autoLoanFooter">
      <Link
        className="personalLoansDetailsLink secondary_text_light"
        onClickCallback={clickDetailsHandler}
        data-testid="plDetailsLink"
        ariaLabel="View more Details"
      >
        <Label
          type="caption"
          color="secondary_text_light"
          bold
          data-testid={datatestid ? `${datatestid}-moreDetails` : null}
          tabIndex="-1"
        >
          View more details
        </Label>
      </Link>
    </Row>
  );
};

const TermValue = ({
  loanValue,
  prefix,
  currentLoanValue,
  postfix,
  current,
  mobile,
  showComparison,
  termName,
  pageType,
  isDetails,
  datatestid,
}) => {
  const showLoanValue = loanValue || loanValue === 0;
  let showDiff = false;
  let diffValue,
    diffValueAbs,
    isPositive,
    diffDisplay = null;
  let termValClass = classnames({
    'Subtitle-1': mobile,
    'text-16': isDetails,
    'h4-loan': true,
    bold: (!showComparison && !mobile) || (showComparison && !current),
    gray: current,
  });
  if (showComparison && !current) {
    if (currentLoanValue || currentLoanValue === 0) {
      diffValue = Number((loanValue - currentLoanValue).toFixed(2));
      isPositive = diffValue < 0;
      diffValueAbs = Math.abs(diffValue);
      showDiff = diffValueAbs !== 0 ? true : false;
      diffDisplay = `${prefix ? prefix : ''}${commafy(diffValueAbs)}${
        postfix ? postfix : ''
      }`;
    }
  }
  datatestid = `${datatestid}-value${current ? '-current' : ''}`;
  return (
    <FlexContainer className="items-align-end ">
      {showLoanValue && (
        <div className={classnames('termValContainer', pageType)}>
          <Label
            datatestid={datatestid}
            className={classnames(termValClass, 'valueLabel', 'noWrapText')}
            color={'fontGray400'}
          >
            {prefix}
            {commafy(loanValue)}
            {postfix}
          </Label>
          <div className="diffTextContainer">
            {showDiff && (
              <DifferentialText
                fontSizeClass={'text-12 noWrap'}
                display={diffDisplay}
                difference={diffValue}
                isPositive={isPositive}
                datatestid={`${datatestid}-diff`}
              />
            )}
          </div>
        </div>
      )}
    </FlexContainer>
  );
};

const AutoLoanTermsColItem = ({
  termsColumnClass,
  bottomPadClass,
  termName,
  loanValue,
  currentLoanValue,
  current,
  showComparison,
  prefix,
  postfix,
  isDetails,
  datatestid,
}) => {
  const hideColItem = !loanValue && loanValue !== 0;
  if (hideColItem) {
    return <></>;
  }
  return (
    <FlexContainer
      className={`justify-between ${termsColumnClass} ${bottomPadClass}`}
    >
      <Label
        datatestid={`${datatestid}-${termName}-name`}
        className="text-12 termName"
      >
        {termName}
        {current ? '' : '*'}
      </Label>
      <TermValue
        prefix={prefix}
        postfix={postfix}
        loanValue={loanValue}
        currentLoanValue={currentLoanValue}
        current={current}
        showComparison={showComparison}
        mobile={true}
        datatestid={`${datatestid}-${termName.replace(/\s+/g, '')}`}
        termName={termName}
        isDetails={isDetails}
      />
    </FlexContainer>
  );
};

const LoanDate = ({ lastRefreshDate }) => {
  return (
    <FlexContainer className="flex-row">
      {lastRefreshDate ? (
        <Label type="text-12" color="fontGray300 noWrapText">
          as of {lastRefreshDate}
        </Label>
      ) : (
        <div className="placeholderDate"></div>
      )}
    </FlexContainer>
  );
};

export const AutoLoanTermsCol = ({
  datatestid,
  showComparison,
  title,
  current,
  monthlyPayment,
  apr,
  term,
  loanAmount,
  currentLoanDetails,
  termsPostfix,
  lastRefreshDate,
  isDetails,
}) => {
  let currentMonthlyPayment,
    currentApr,
    currentTerm,
    currentAmount = null;
  let titleClass = classnames('bold', {
    overline: !isDetails,
    fontGray300: current,
    fontGray400: !current,
    'text-10': !isDetails,
    'text-14': isDetails,
  });
  let termsColumnClass = classnames({
    'flex-row': !showComparison,
    'flex-column': showComparison,
  });
  let bottomPadClass = classnames({
    padB12: !showComparison,
    padB16: showComparison,
  });
  let amountTermName = 'Loan Amount';
  if (showComparison) {
    if (!current) {
      titleClass = `${titleClass}`;
      if (currentLoanDetails) {
        currentMonthlyPayment = currentLoanDetails.monthlyPayment;
        currentApr = currentLoanDetails.apr;
        currentTerm = currentLoanDetails.term;
        currentAmount = currentLoanDetails.balance;
      }
    }
  }
  return (
    <FlexContainer className={`autoLoanTitleColumn flex-column`}>
      {showComparison && (
        <>
          <Label
            datatestid={`${datatestid}-${title}-title`}
            className={titleClass}
          >
            {title}
          </Label>
          {isDetails && <LoanDate lastRefreshDate={lastRefreshDate} />}
          <div className="padB12"></div>
        </>
      )}
      <AutoLoanTermsColItem
        termsColumnClass={termsColumnClass}
        bottomPadClass={bottomPadClass}
        termName={'Monthly Payment'}
        loanValue={monthlyPayment}
        currentLoanValue={currentMonthlyPayment}
        current={current}
        showComparison={showComparison}
        prefix={'$'}
        isDetails={isDetails}
        datatestid={datatestid}
      />
      <AutoLoanTermsColItem
        termsColumnClass={termsColumnClass}
        bottomPadClass={bottomPadClass}
        termName={'APR'}
        loanValue={apr}
        currentLoanValue={currentApr}
        current={current}
        showComparison={showComparison}
        postfix={'%'}
        isDetails={isDetails}
        datatestid={datatestid}
      />
      <AutoLoanTermsColItem
        termsColumnClass={termsColumnClass}
        bottomPadClass={bottomPadClass}
        termName={'Term'}
        loanValue={term}
        currentLoanValue={currentTerm}
        current={current}
        showComparison={showComparison}
        postfix={termsPostfix}
        isDetails={isDetails}
        datatestid={datatestid}
      />
      <AutoLoanTermsColItem
        termsColumnClass={termsColumnClass}
        bottomPadClass={bottomPadClass}
        termName={amountTermName}
        loanValue={loanAmount}
        currentLoanValue={currentAmount}
        current={current}
        showComparison={showComparison}
        prefix={'$'}
        isDetails={isDetails}
        datatestid={datatestid}
      />
    </FlexContainer>
  );
};

const AutoLoanTermTitleRow = ({ showComparison, rowType, datatestid }) => {
  return (
    <Row className={`autoLoanTitleRow ${rowType} carousel`}>
      <FlexContainer className={`flex-row autoLoanContainer termsRowDesktop`}>
        {showComparison && <div></div>}
        <Label
          data-testid={`${datatestid}-MonthlyPaymentTitle`}
          className="text-12"
          color="fontGray300"
        >
          Monthly Payment*
        </Label>
        <Label
          data-testid={`${datatestid}-AprTitle`}
          className="text-12"
          color="fontGray300"
        >
          APR*
        </Label>
        <Label
          data-testid={`${datatestid}-TermTitle`}
          className="text-12"
          color="fontGray300"
        >
          Term*
        </Label>
        <Label
          data-testid={`${datatestid}-AmountTitle`}
          className="text-12"
          color="fontGray300"
        >
          Loan Amount*
        </Label>
      </FlexContainer>
    </Row>
  );
};

export const AutoLoanTermRow = ({
  datatestid,
  title,
  monthlyPayment,
  apr,
  term,
  loanAmount,
  current,
  currentLoanDetails,
  showComparison,
  termsPostfix,
  rowType,
  pageType,
  isDetails,
  lastRefreshDate,
}) => {
  let currentMonthlyPayment,
    currentApr,
    currentTerm,
    currentAmount = null;
  let titleClass = classnames('bold', { Overline: !isDetails, gray: current });
  if (!current && currentLoanDetails) {
    currentMonthlyPayment = currentLoanDetails.monthlyPayment;
    currentApr = currentLoanDetails.apr;
    currentTerm = currentLoanDetails.term;
    currentAmount = currentLoanDetails.balance;
  }
  return (
    <Row className={`autoLoanRow ${rowType} carousel`}>
      <FlexContainer
        className={`flex-row autoLoanContainer termsRowDesktop format100`}
      >
        {showComparison && (
          <FlexContainer className="flex-column">
            <Label
              datatestid={`${datatestid}-${title}-title`}
              className={titleClass}
            >
              {title}
            </Label>
            {isDetails && <LoanDate lastRefreshDate={lastRefreshDate} />}
          </FlexContainer>
        )}
        <TermValue
          prefix={'$'}
          loanValue={monthlyPayment}
          currentLoanValue={currentMonthlyPayment}
          current={current}
          showComparison={showComparison}
          pageType={pageType}
          datatestid={`${datatestid}-monthlyPayment`}
        />
        <TermValue
          currentLoanValue={currentApr}
          loanValue={apr}
          postfix={'%'}
          current={current}
          showComparison={showComparison}
          pageType={pageType}
          datatestid={`${datatestid}-apr`}
        />
        <TermValue
          loanValue={term}
          currentLoanValue={currentTerm}
          postfix={termsPostfix}
          current={current}
          showComparison={showComparison}
          pageType={pageType}
          datatestid={`${datatestid}-term`}
        />
        <TermValue
          prefix={'$'}
          loanValue={loanAmount}
          currentLoanValue={currentAmount}
          current={current}
          showComparison={showComparison}
          pageType={pageType}
          datatestid={`${datatestid}-loanAmount`}
        />
      </FlexContainer>
    </Row>
  );
};

export const AutoLoanOfferBodyMobile = ({
  datatestid,
  currentLoanDetails,
  showComparison,
  apr,
  term,
  loanAmount,
  monthlyPayment,
  termsPostfix,
  isDetails,
}) => {
  let mainTermsColClass = classnames({ width100: !showComparison });
  let currentTitle = isDetails ? 'Current loan' : 'CURRENT';
  let thisOfferTitle = isDetails ? 'This offer' : 'THIS OFFER';
  return (
    <FlexContainer className="format100">
      {showComparison && (
        <div className="format50">
          <AutoLoanTermsCol
            current={true}
            title={currentTitle}
            showComparison={showComparison}
            apr={currentLoanDetails.apr?.toFixed(2)}
            term={currentLoanDetails.term}
            loanAmount={currentLoanDetails.balance}
            monthlyPayment={currentLoanDetails.monthlyPayment}
            lastRefreshDate={currentLoanDetails.lastRefreshDate}
            datatestid={datatestid}
            termsPostfix={termsPostfix}
            isDetails={isDetails}
          />
        </div>
      )}
      <div className={mainTermsColClass}>
        <AutoLoanTermsCol
          title={thisOfferTitle}
          showComparison={showComparison}
          apr={apr}
          term={term}
          loanAmount={loanAmount}
          monthlyPayment={monthlyPayment}
          datatestid={datatestid}
          currentLoanDetails={currentLoanDetails}
          termsPostfix={termsPostfix}
          isDetails={isDetails}
        />
      </div>
    </FlexContainer>
  );
};

export const AutoLoanOfferBody = ({
  datatestid,
  currentLoanDetails,
  showComparison,
  apr,
  term,
  loanAmount,
  monthlyPayment,
  termsPostfix,
  rowType,
  pageType,
  isDetails,
}) => {
  let currentTitle = isDetails ? 'Current loan' : 'CURRENT';
  let thisOfferTitle = isDetails ? 'This offer' : 'THIS OFFER';

  return (
    <FlexContainer className="flex-column">
      <AutoLoanTermTitleRow
        showComparison={showComparison}
        datatestid={datatestid}
        rowType={rowType}
      />
      {showComparison && (
        <AutoLoanTermRow
          current={true}
          title={currentTitle}
          apr={currentLoanDetails.apr?.toFixed(2)}
          term={currentLoanDetails.term}
          loanAmount={currentLoanDetails.balance}
          monthlyPayment={currentLoanDetails.monthlyPayment}
          currentLoanDetails={currentLoanDetails}
          datatestid={datatestid}
          showComparison={showComparison}
          termsPostfix={termsPostfix}
          rowType={rowType}
          pageType={pageType}
          isDetails={isDetails}
          lastRefreshDate={currentLoanDetails.lastRefreshDate}
        />
      )}
      <AutoLoanTermRow
        title={thisOfferTitle}
        apr={apr}
        term={term}
        loanAmount={loanAmount}
        monthlyPayment={monthlyPayment}
        currentLoanDetails={currentLoanDetails}
        datatestid={datatestid}
        showComparison={showComparison}
        termsPostfix={termsPostfix}
        rowType={rowType}
        pageType={pageType}
        isDetails={isDetails}
      />
      <div className="padT20"></div>
    </FlexContainer>
  );
};

const AutoLoanBulletsView = ({ offerMetadata, datatestid, loanType }) => {
  let bulletList = offerMetadata.purchaseBullets;
  if (
    (loanType === 'refinanceLoan' || bulletList.length === 0) &&
    offerMetadata.refinanceBullets.length > 0
  ) {
    bulletList = offerMetadata.refinanceBullets;
  }
  return (
    <Row className="mobilePadL24 desktopPadL0">
      <div className="bulletContainer">
        <ul>
          {bulletList.length > 0
            ? bulletList.map((item, index) => {
                return (
                  <BulletText
                    key={`${datatestid}-itemKey-${index}`}
                    datatestid={datatestid ? `${datatestid}` : null}
                    text={item}
                    index={index}
                    sizeClass={'mediumText'}
                  />
                );
              })
            : null}
        </ul>
      </div>
    </Row>
  );
};

const StandardAutoLoanOfferMobile = ({
  clickDetailsHandler,
  clickApplyHandler,
  showComparison,
  currentLoanDetails,
  offerMetadata,
  termsPostfix,
  hideDetails,
  loanType,
  seenPixel,
  offerImageLinkUrlProxy,
}) => {
  const { provider, apr, term, loanAmount, monthlyPayment } = offerMetadata;
  const datatestid = `${provider.lenderName.replace(
    /\s+/g,
    '',
  )}-AutoLoanOffer-Mobile`;
  return (
    <SectionContainer className="AutoLoanOfferModule mobile grayShadow offerCard">
      <Row>
        <AutoLoanOfferImage
          datatestid={datatestid}
          clickApplyHandler={clickApplyHandler}
          imageLink={offerImageLinkUrlProxy}
        />
      </Row>
      {hideDetails ? (
        <AutoLoanBulletsView
          offerMetadata={offerMetadata}
          loanType={loanType}
        />
      ) : (
        <Row>
          <AutoLoanOfferBodyMobile
            apr={apr}
            loanAmount={loanAmount}
            term={term}
            monthlyPayment={monthlyPayment}
            currentLoanDetails={currentLoanDetails}
            showComparison={showComparison}
            termsPostfix={termsPostfix}
            datatestid={datatestid}
          />
        </Row>
      )}
      <Row>
        <AutoLoanOfferApply
          datatestid={datatestid}
          clickApplyHandler={clickApplyHandler}
          name={provider.lenderName}
        />
        <div className="padT20">{seenPixel}</div>
        <AutoLoanOfferFooter clickDetailsHandler={clickDetailsHandler} />
      </Row>
    </SectionContainer>
  );
};

const StandardAutoLoanOfferDesktop = ({
  clickDetailsHandler,
  clickApplyHandler,
  showComparison,
  currentLoanDetails,
  offerMetadata,
  termsPostfix,
  hideDetails,
  loanType,
  seenPixel,
  offerImageLinkUrlProxy,
}) => {
  const { provider, apr, term, loanAmount, monthlyPayment } = offerMetadata;
  const datatestid = `${provider.lenderName.replace(
    /\s+/g,
    '',
  )}-AutoLoanOffer-Desktop`;
  return (
    <SectionContainer className="AutoLoanOfferModule desktop grayShadow offerCard">
      <AutoLoanOfferHeader
        datatestid={datatestid}
        clickDetailsHandler={clickDetailsHandler}
        imageLink={offerImageLinkUrlProxy}
        clickApplyHandler={clickApplyHandler}
        name={provider.lenderName}
      />
      {hideDetails ? (
        <AutoLoanBulletsView
          offerMetadata={offerMetadata}
          loanType={loanType}
        />
      ) : (
        <AutoLoanOfferBody
          datatestid={datatestid}
          apr={apr}
          loanAmount={loanAmount}
          term={term}
          monthlyPayment={monthlyPayment}
          currentLoanDetails={currentLoanDetails}
          showComparison={showComparison}
          termsPostfix={termsPostfix}
        />
      )}
      {seenPixel}
      <AutoLoanOfferFooter clickDetailsHandler={clickDetailsHandler} />
    </SectionContainer>
  );
};

const StandardAutoLoanOffer = props => {
  return (
    <>
      <StandardAutoLoanOfferDesktop {...props} />
      <StandardAutoLoanOfferMobile {...props} />
    </>
  );
};

export {
  StandardAutoLoanOffer,
  StandardAutoLoanOfferDesktop,
  StandardAutoLoanOfferMobile,
};
